import React from 'react';
import { createRoot } from 'react-dom/client';

import readAttribute, {
  readBooleanAttribute
} from 'common/tools/dom/readAttribute';
import trans from 'common/tools/translations/trans';

import opinionBoosterHoc from 'website/components/Tooltip/opinionBoosterHoc';
import UserOpinionBanner, {
  UserOpinionBannerProps
} from 'website/components/UserOpinion/UserOpinionBanner';
import UserReviewOverview, {
  UserReviewOverviewProps
} from 'website/components/UserOpinion/UserReviewOverview';
import allocineContainer from 'website/containers/allocineContainer';

type CommonProps = {
  entityId: string;
  title?: string;
  displayMode?: 'list' | 'default';
  withSuggestions?: boolean;
};

type EntryConfig = {
  className: string;
  Component: React.FC<UserOpinionBannerProps | UserReviewOverviewProps>;
};

const entries: EntryConfig[] = [
  {
    className: 'js-user-opinion-banner-entry',
    Component: UserOpinionBanner
  },
  {
    className: 'js-user-review-overview-entry',
    Component: UserReviewOverview
  }
];

function createEntryWrapper<T extends CommonProps>(
  WrappedComponent: React.FC<T>
): React.FC<T> {
  const WrappedWithDisplayName: React.FC<T> = (props: T) => (
    <WrappedComponent {...props} />
  );
  WrappedWithDisplayName.displayName = `EntryWrapper(${WrappedComponent.displayName || WrappedComponent.name || 'DynamicComponentWrapper'})`;
  return WrappedWithDisplayName;
}

export default function userBannerOpinionEntry() {
  for (const { className, Component } of entries) {
    const anchors = document.getElementsByClassName(className);

    for (const anchor of anchors) {
      const entityId = readAttribute<string>(anchor, 'data-entity-id');
      if (!entityId) continue;

      const title = readAttribute<string>(anchor, 'data-title');
      const rawDisplayMode = readAttribute<string>(anchor, 'data-mode');
      const displayMode: 'list' | 'default' | undefined =
        rawDisplayMode === 'list' || rawDisplayMode === 'default'
          ? rawDisplayMode
          : undefined;

      const withSuggestions = readBooleanAttribute(
        anchor,
        'data-show-suggestion',
        true
      );

      const Wrapped = createEntryWrapper(Component);

      const ConnectedComponent = allocineContainer<CommonProps>(
        opinionBoosterHoc(
          Wrapped,
          anchor,
          'js-tooltip-opinion-booster',
          <span>{trans('tooltip-booster.opinion')}</span>
        )
      );

      const root = createRoot(anchor);
      root.render(
        <ConnectedComponent
          entityId={entityId}
          title={title}
          displayMode={displayMode}
          withSuggestions={withSuggestions}
        />
      );
    }
  }
}
